import React from "react";
import {BooleanRadioButton, BooleanRadioButtonGroup} from "@octopusdeploy/design-system-components";
import {DataBaseComponent, DataBaseComponentState, Errors} from "components/DataBaseComponent";
import repository from "client/repository";
import {BusyState} from "components/BusyFromPromise/BusyFromPromise";
import {HostedInstanceResource} from "client/resources/hostedInstanceResource";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {HostedInstanceConfigureExecutionsReplicasRequest} from "../../../client/resources/HostedInstanceConfigureExecutionsReplicasRequest";
import {Number} from "../../../shared/Number";
import { Callout } from "@octopusdeploy/design-system-components";

interface ConfigureInstanceExecutionsReplicasDialogProps {
    errors: Errors;
    busy: BusyState;
    instance: HostedInstanceResource;
    isModularExecutionsEnabled: boolean;
    isExecutionsHPAEnabled: boolean;
    isRunningInProduction: boolean;
    onSaveClick(): Promise<boolean>;
}

interface ConfigureInstanceExecutionsReplicasDialogState extends DataBaseComponentState {
    replicaRequest: Partial<HostedInstanceConfigureExecutionsReplicasRequest>;
}

export class ConfigureInstanceExecutionsReplicasDialog extends DataBaseComponent<ConfigureInstanceExecutionsReplicasDialogProps, ConfigureInstanceExecutionsReplicasDialogState> {
    // Mirrors CloudPortal.Core/Limits.cs ExecutionsMinimumReplicas/ExecutionsMaximumReplicas — keep in sync with the backend limit.
    private static readonly MinExecutionsReplicas = 1;
    private static readonly MaxExecutionsReplicas = 3;

    constructor(props: ConfigureInstanceExecutionsReplicasDialogProps) {
        super(props);
        this.state = {
            replicaRequest: {
                MinimumReplicaCount: this.props.instance.ExecutionsMinimumReplicas,
                MaximumReplicaCount: this.props.instance.ExecutionsMaximumReplicas,
                TargetCPUAverageUtilization: this.props.instance.ExecutionsHPATargetCPUAverageUtilization,
                ReprovisionDuringMaintenanceWindow: true
            }
        };
    }

    render() {
        const request = this.state.replicaRequest;
        const validationError = this.getValidationError();
        return <SaveDialogLayout title={"Configure Executions Replicas"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonDisabled={this.replicasAreUnchanged() || !!validationError}
                                 onSaveClick={() => this.setExecutionsReplicas(request)}>

            { !this.props.isModularExecutionsEnabled &&
                <Callout type="warning" title={"Warning"}>
                    This instance does <strong>not</strong> have Modular Executions enabled. Changes made here will have no effect until Modular Executions is enabled.
                </Callout> }

            { !this.props.isExecutionsHPAEnabled &&
                <Callout type="warning" title={"Warning"}>
                    This instance does <strong>not</strong> have Executions HPA enabled. Only the minimum replicas here will have an effect until Executions HPA is enabled.
                </Callout> }

            { validationError &&
                <Callout type="danger" title={"Validation Error"}>
                    {validationError}
                </Callout> }

            <Number label="Minimum Replicas"
                  value={request.MinimumReplicaCount}
                  onChange={MinimumReplicaCount => this.updateExecutionsReplicaRequest({MinimumReplicaCount})}
                  min={ConfigureInstanceExecutionsReplicasDialog.MinExecutionsReplicas}
                  max={this.props.isRunningInProduction ? ConfigureInstanceExecutionsReplicasDialog.MaxExecutionsReplicas : undefined}
            />

            <Number label="Maximum Replicas"
                  value={request.MaximumReplicaCount}
                  onChange={MaximumReplicaCount => this.updateExecutionsReplicaRequest({MaximumReplicaCount})}
                  min={ConfigureInstanceExecutionsReplicasDialog.MinExecutionsReplicas}
                  max={this.props.isRunningInProduction ? ConfigureInstanceExecutionsReplicasDialog.MaxExecutionsReplicas : undefined}
            />

            <Number label="Target CPU Utilization (%)"
                  value={request.TargetCPUAverageUtilization}
                  onChange={TargetCPUAverageUtilization => this.updateExecutionsReplicaRequest({TargetCPUAverageUtilization})}
                  min={1}
                  max={100}
            />

            <BooleanRadioButtonGroup value={this.state.replicaRequest.ReprovisionDuringMaintenanceWindow}
                              onChange={ReprovisionDuringMaintenanceWindow => this.updateExecutionsReplicaRequest({ReprovisionDuringMaintenanceWindow})}>
                <BooleanRadioButton value={true} label="Apply During Outage Window"/>
                <BooleanRadioButton value={false} label="Apply Immediately"/>
            </BooleanRadioButtonGroup>

        </SaveDialogLayout>;
    }

    private getValidationError(): string | null {
        const request = this.state.replicaRequest;
        
        if (request.MinimumReplicaCount !== undefined && request.MinimumReplicaCount < ConfigureInstanceExecutionsReplicasDialog.MinExecutionsReplicas) {
            return `Minimum replicas must be at least ${ConfigureInstanceExecutionsReplicasDialog.MinExecutionsReplicas}.`;
        }
        if (this.props.isRunningInProduction &&
            request.MaximumReplicaCount !== undefined &&
            request.MaximumReplicaCount > ConfigureInstanceExecutionsReplicasDialog.MaxExecutionsReplicas) {
            return `Maximum replicas cannot exceed ${ConfigureInstanceExecutionsReplicasDialog.MaxExecutionsReplicas} in Production.`;
        }
        if (request.MaximumReplicaCount !== undefined && request.MinimumReplicaCount !== undefined) {
            if (request.MaximumReplicaCount < request.MinimumReplicaCount) {
                return "Maximum replicas must be greater than or equal to minimum replicas.";
            }
        }
        if (request.TargetCPUAverageUtilization !== undefined) {
            if (request.TargetCPUAverageUtilization < 1 || request.TargetCPUAverageUtilization > 100) {
                return "Target CPU utilization must be between 1 and 100.";
            }
        }
        return null;
    }

    private replicasAreUnchanged() {
        return this.state.replicaRequest.MinimumReplicaCount == this.props.instance.ExecutionsMinimumReplicas &&
               this.state.replicaRequest.MaximumReplicaCount == this.props.instance.ExecutionsMaximumReplicas &&
               this.state.replicaRequest.TargetCPUAverageUtilization == this.props.instance.ExecutionsHPATargetCPUAverageUtilization;
    }

    private updateExecutionsReplicaRequest(update: Partial<HostedInstanceConfigureExecutionsReplicasRequest>) {
        return this.setState(prevState => ({replicaRequest: {...prevState.replicaRequest, ...update}}));
    }

    private setExecutionsReplicas(request: Partial<HostedInstanceConfigureExecutionsReplicasRequest>) {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.configureExecutionsReplicas(this.props.instance, request as HostedInstanceConfigureExecutionsReplicasRequest);
            await this.props.onSaveClick();
            return true;
        });
    }
}
